import {
  createContext,
  type PropsWithChildren,
  useContext,
  useReducer,
} from 'react';

import { useDeserializePageStateFromUrl } from './hooks/useDeserializePageStateFromUrl';
import { useSyncPageStateToUrl } from './hooks/useSyncPageStateToUrl';
import { type PageStateAction, pageStateReducer } from './page-state-reducer';
import type { PageState } from './types';

type PageStateContextType = {
  pageState: PageState;
  dispatch: React.Dispatch<PageStateAction>;
};

const PageStateContext = createContext<PageStateContextType | undefined>(
  undefined
);

type PageStateProviderProps = PropsWithChildren & {
  initialPageState: PageState;
  /** When false, initial URL `pageState` is ignored (isolated preview / embedded contexts). */
  hydratePageStateFromUrl?: boolean;
  /** When false, page state changes are not written to the location search params. */
  syncPageStateToUrl?: boolean;
};

export const PageStateProvider = ({
  children,
  initialPageState,
  hydratePageStateFromUrl = true,
  syncPageStateToUrl = true,
}: PageStateProviderProps): JSX.Element => {
  const pageStateFromUrl = useDeserializePageStateFromUrl(
    hydratePageStateFromUrl
  );

  const [pageState, dispatch] = useReducer(
    pageStateReducer,
    pageStateFromUrl ?? initialPageState
  );

  useSyncPageStateToUrl(pageState, syncPageStateToUrl);

  return (
    <PageStateContext.Provider value={{ pageState, dispatch }}>
      {children}
    </PageStateContext.Provider>
  );
};

export const usePageState = () => {
  const context = useContext(PageStateContext);
  if (!context) {
    throw new Error('usePageState must be used within a PageStateProvider');
  }
  return context;
};
